import {
  ActionDropdown,
  ActionDropdownTypes,
  Button,
  Checkbox,
  ErrorBoundary,
  Icon,
  Notice,
  Shadow,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Pressable, Box } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  CustomerDetailStackParamList,
  SignedInNavigationScreenNavigationProp,
} from '../../../../../../routes/types';
import {
  customerDetailProfitabilityChartAssetsDatapointsQuery,
  customerDetailProfitabilityChartAssetsQuery,
} from '../../../../../../store/customerDetail/selector';
import { FeatureToggle, useUnleashFlags } from '../../../../../../utils/useUnleashFlags';

import { axisDefaultConfig, buildChartData, lineDefaultConfig } from './chart';
import {
  AssetCompareOption,
  assetCompareOptions,
  assetIntervalFilters,
  chartTags,
  defaultAssetCompareOptions,
  noticeRequiredAttentionSection,
} from './constants';
import { ProfitabilityChartSkeleton } from './skeleton';
import { LineChartTooltip } from './tooltipContent';

const mobileTriggerProps: ActionDropdownTypes.Props['triggerProps'] = {
  color: 'neutral.black',
  padding: Spacing['inline.quarck'],
  borderRadius: BoxRadius.sm,
  style: {
    shadowColor: '#00000014',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 1,
  },
};

export const ProfitabilityChart: React.FC = () => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const [currentAssetLabel, setCurrentAssetLabel] = useState<string>('');
  const [currentAssetType, setCurrentAssetType] = useState<string | null>(null);
  const [currentAssetId, setCurrentAssetId] = useState<number>(-1);
  const [currentInterval, setCurrentInterval] = useState<number>(0);
  const [selectedCompareOptions, setSelectedCompareOptions] = useState<AssetCompareOption[]>(
    defaultAssetCompareOptions
  );
  const { b2bShowProfitabilityReportV2 } = useUnleashFlags(FeatureToggle.ProfitabilityReportV2);
  const {
    data: assetTypes,
    loading: assetTypesLoading,
    error: assetTypesError,
    refresher: assetTypesRefresher,
  } = useRecoilQuery(customerDetailProfitabilityChartAssetsQuery);

  const {
    data: assetData,
    loading: assetDataLoading,
    error: assetDataError,
    refresher: assetDataRefresher,
  } = useRecoilQuery(
    customerDetailProfitabilityChartAssetsDatapointsQuery({
      accountId: route.params.accountId,
      assetTypeId: currentAssetId,
    })
  );

  const handleProfitabilityReportDownload = useCallback(() => {
    navigation.navigate('ProfitabilityReportDownload', {
      accountNumber: route.params.accountNumber,
      customerId: route.params.customerId,
    });
  }, [route.params.accountNumber, navigation]);

  const chartData = buildChartData(assetData, currentInterval);
  const isChecked = useMemo(
    () => ({
      Asset: selectedCompareOptions.some((item) => item.value === 'Asset'),
      InterbankRate: selectedCompareOptions.some((item) => item.value === 'InterbankRate'),
      CheckingsAccounts: selectedCompareOptions.some((item) => item.value === 'CheckingsAccounts'),
      BovespaIndex: selectedCompareOptions.some((item) => item.value === 'BovespaIndex'),
      ConsumerPriceIndex: selectedCompareOptions.some(
        (item) => item.value === 'ConsumerPriceIndex'
      ),
    }),
    [selectedCompareOptions]
  );

  const hasData = !assetDataLoading && chartData.length > 0;

  const extendedChartsTags = useMemo(
    () => ({
      ...chartTags,
      Asset: currentAssetLabel,
    }),
    [currentAssetLabel]
  );

  const assetCompareOptionsNormalized = useMemo(
    () =>
      assetCompareOptions.map((item) => ({
        ...item,
        label: item.label.replace('%asset', currentAssetLabel),
      })),
    [currentAssetLabel]
  );

  const namesMap = useMemo(
    () => new Map<string, string>(chartData.map(({ month, fullMonth }) => [month, fullMonth])),
    [chartData]
  );

  let chartContent: JSX.Element;
  const handleAssetTypeChange = useCallback((label: string, id: number, type: string) => {
    setCurrentAssetLabel(label);
    setCurrentAssetId(id);
    setCurrentAssetType(type);
  }, []);

  const handleSelectCompareOptions = useCallback(
    (value: string, selected: boolean) => {
      if (selected) {
        const found = assetCompareOptions.find((item) => item.value === value);

        if (found) {
          setSelectedCompareOptions([...selectedCompareOptions, found]);
        }
      } else {
        setSelectedCompareOptions(selectedCompareOptions.filter((item) => item.value !== value));
      }
    },
    [selectedCompareOptions]
  );

  const assetTypeList = useMemo(
    () =>
      assetTypes?.map((item, index) => (
        <ActionDropdown.Item
          key={index.toString()}
          label={item.assetTypeDescriptionNormalized}
          labelProps={{ flex: 1, numberOfLines: 1 }}
          icon={item.assetTypeId === currentAssetId ? 'RadioChecked' : 'RadioUnchecked'}
          stackProps={{ flexDirection: { base: 'row-reverse', sm: 'row' } }}
          onPress={() =>
            handleAssetTypeChange(
              item.assetTypeDescriptionNormalized,
              item.assetTypeId,
              item.assetType
            )
          }
        />
      )) ?? [],
    [assetTypes, currentAssetId, handleAssetTypeChange]
  );

  const assetCompareActions = useMemo(
    () =>
      assetCompareOptionsNormalized.map((item, index) => (
        <ActionDropdown.Item
          key={index.toString()}
          label={item.label}
          labelProps={{ flex: 1 }}
          icon={isChecked[item.value] ? 'RadioChecked' : 'RadioUnchecked'}
          stackProps={{ flexDirection: { base: 'row-reverse', sm: 'row' } }}
          onPress={() => handleSelectCompareOptions(item.value, !isChecked[item.value])}
        />
      )),
    [assetCompareOptionsNormalized, handleSelectCompareOptions, isChecked]
  );

  const assetCompareLabel = useMemo(() => {
    const selectedMobileCompareOptions = assetCompareOptions.filter(
      (item) => isChecked[item.value]
    );

    if (selectedMobileCompareOptions.length === 1) {
      return selectedMobileCompareOptions[0].label;
    } else if (selectedMobileCompareOptions.length > 1) {
      return `${selectedMobileCompareOptions.length} selecionados`;
    } else {
      return 'Selecione';
    }
  }, [isChecked]);

  if (assetTypesError) {
    chartContent = <ErrorBoundary onRefresh={assetTypesRefresher} />;
  } else if (assetDataError) {
    chartContent = <ErrorBoundary onRefresh={assetDataRefresher} />;
  } else {
    const filterButtons = assetIntervalFilters.map((item, index) => {
      const pressable = (
        <Pressable onPress={() => setCurrentInterval(index)} padding={Spacing['inline.nano']}>
          <Text
            color={currentInterval === index ? 'neutral.white' : 'neutral.black'}
            fontSize={{ base: Typography['fontsize.md'], sm: Typography['fontsize.sm'] }}>
            <Text display={{ base: 'none', sm: 'flex' }}>{item.label}</Text>
            <Text display={{ base: 'flex', sm: 'none' }}>{item.labelMobile}</Text>
          </Text>
        </Pressable>
      );

      if (currentInterval === index) {
        return (
          <Box
            backgroundColor={{
              base: Color['brand.tertiary.principal'],
              sm: Color['brand.primary.principal'],
            }}
            paddingX={Spacing['inline.nano']}
            borderRadius="full"
            key={index.toString()}>
            {pressable}
          </Box>
        );
      } else {
        return (
          <Shadow
            key={index.toString()}
            level={1}
            borderRadius="full"
            paddingX={Spacing['inline.nano']}>
            {pressable}
          </Shadow>
        );
      }
    });

    chartContent = (
      <>
        {!assetDataLoading && (
          <HStack display={{ base: 'none', sm: 'flex' }} space={Spacing['stack.xxxs']}>
            {filterButtons}
          </HStack>
        )}

        <HStack display={{ base: 'flex', sm: 'none' }} justifyContent="space-between">
          <VStack space={Spacing['stack.nano']}>
            <Text color="neutral.medium">Visualize por:</Text>
            <ActionDropdown
              isLoading={assetTypesLoading}
              triggerLabel={currentAssetLabel}
              triggerProps={mobileTriggerProps}
              placement="bottom right">
              {assetTypeList}
            </ActionDropdown>
          </VStack>
          <VStack space={Spacing['stack.nano']}>
            <Text color="neutral.medium">Comparar:</Text>
            <ActionDropdown
              isLoading={assetDataLoading}
              triggerLabel={assetCompareLabel}
              triggerProps={mobileTriggerProps}>
              {assetCompareActions}
            </ActionDropdown>
          </VStack>
        </HStack>

        {!assetDataLoading && chartData.length === 0 && (
          <VStack
            space={Spacing['stack.nano']}
            alignItems="center"
            flex={1}
            minHeight="310px"
            justifyContent="center">
            <Icon
              testID="info-icon-no-assets"
              name="Info"
              size="md"
              color={Color['neutral.medium']}
            />
            <Text variant="bold">Não foi possível gerar o gráfico</Text>
            <Text>Não há dados suficiente para construir o gráfico.</Text>
          </VStack>
        )}

        {hasData && (
          <Box flex={1}>
            <ResponsiveContainer width="100%" minHeight={265} height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="month" {...axisDefaultConfig} />
                <YAxis
                  tickFormatter={(n) => Utils.Mask.Percentage(n)}
                  axisLine={false}
                  {...axisDefaultConfig}
                />
                <CartesianGrid strokeDasharray={6} />
                <Tooltip
                  content={({ active, label, payload }) => (
                    <LineChartTooltip
                      key={String(label)}
                      namesMap={namesMap}
                      tags={extendedChartsTags}
                      label={label}
                      active={active}
                      payload={payload}
                    />
                  )}
                />

                {isChecked.InterbankRate && (
                  <Line
                    key="reachart-line-cdi"
                    dataKey="InterbankRate"
                    stroke={Color['linechart.bench1']}
                    {...lineDefaultConfig}
                    activeDot={{ r: 0 }}
                  />
                )}

                {isChecked.CheckingsAccounts && (
                  <Line
                    key="reachart-line-poupanca"
                    dataKey="CheckingsAccounts"
                    stroke={Color['linechart.bench2']}
                    {...lineDefaultConfig}
                    activeDot={{ r: 0 }}
                  />
                )}

                {isChecked.BovespaIndex && (
                  <Line
                    key="reachart-line-ibov"
                    dataKey="BovespaIndex"
                    stroke={Color['linechart.bench3']}
                    {...lineDefaultConfig}
                    activeDot={{ r: 0 }}
                  />
                )}

                {isChecked.ConsumerPriceIndex && (
                  <Line
                    key="reachart-line-ipca"
                    dataKey="ConsumerPriceIndex"
                    stroke={Color['linechart.bench4']}
                    {...lineDefaultConfig}
                    activeDot={{ r: 0 }}
                  />
                )}

                {isChecked.Asset && (
                  <Line
                    key="reachart-line-asset"
                    dataKey="Asset"
                    stroke={Color['brand.primary.principal']}
                    {...lineDefaultConfig}
                    activeDot={{ r: 6 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        {hasData && (
          <HStack
            display={{ base: 'none', sm: 'flex' }}
            space={Spacing['stack.nano']}
            justifyContent="space-between">
            <VStack space={Spacing['stack.nano']}>
              <Text>Classe de ativo</Text>
              <Checkbox
                testId="compart-options-asset-class"
                options={[assetCompareOptionsNormalized[0]]}
                selectedValues={selectedCompareOptions.map((item) => item.value)}
                onChange={handleSelectCompareOptions}
              />
            </VStack>
            <VStack space={Spacing['stack.nano']}>
              <Text>Comparativos</Text>
              <Checkbox
                testId="compart-options-checkbox"
                options={assetCompareOptionsNormalized.toSpliced(0, 1)}
                selectedValues={selectedCompareOptions.map((item) => item.value)}
                onChange={handleSelectCompareOptions}
              />
            </VStack>
          </HStack>
        )}
        {hasData && (
          <HStack display={{ base: 'flex', sm: 'none' }} justifyContent="space-between">
            {filterButtons}
          </HStack>
        )}
      </>
    );
  }

  /**
   * Set the first asset type as the default
   */
  useEffect(() => {
    if (assetTypes?.length && !currentAssetLabel) {
      const [firstAsset] = assetTypes;
      handleAssetTypeChange(
        firstAsset.assetTypeDescriptionNormalized,
        firstAsset.assetTypeId,
        firstAsset.assetType
      );
    }
  }, [assetTypes, currentAssetLabel, handleAssetTypeChange]);

  return (
    <Shadow level={1} padding={Spacing['inline.xxxs']} borderRadius={BoxRadius.sm} flex={1}>
      <VStack space={Spacing['stack.nano']} flex={1}>
        <HStack justifyContent="space-between">
          <Text fontSize={Typography['fontsize.lg']} variant="bold">
            Rentabilidade
          </Text>
          <HStack space={Spacing['stack.xxxs']}>
            {b2bShowProfitabilityReportV2 && (
              <HStack space={Spacing['stack.nano']} alignItems="center">
                <Text display={{ base: 'none', sm: 'flex' }} color="neutral.medium">
                  Relatório Simplificado:
                </Text>
                <Button
                  variant="textLink"
                  onPress={handleProfitabilityReportDownload}
                  p={0}
                  rightIcon={
                    <Icon
                      testID="export-button-profitability"
                      name="Pdf"
                      size="xxs"
                      color={Color['brand.tertiary.principal']}
                    />
                  }>
                  EXPORTAR
                </Button>
              </HStack>
            )}
            {!assetTypesError && (
              <HStack display={{ base: 'none', sm: 'flex' }} space={Spacing['stack.nano']}>
                <Text color="neutral.medium">Visualize por:</Text>
                <ActionDropdown
                  isLoading={assetTypesLoading}
                  triggerLabel={currentAssetLabel}
                  triggerLabelColor="neutral.black">
                  {assetTypeList}
                </ActionDropdown>
              </HStack>
            )}
          </HStack>
        </HStack>
        {currentAssetType === 'FixedIncome' && (
          <Notice
            my={Spacing['stack.nano']}
            textObjects={noticeRequiredAttentionSection[currentAssetType]}
            testId="notice"
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity('feedback.attention.lightest', 0.4)}
          />
        )}
        {!assetDataLoading && chartContent}
        {assetDataLoading && <ProfitabilityChartSkeleton />}
      </VStack>
    </Shadow>
  );
};
